// Phase 162 — the student's question-type practice library.
//
// House pattern: plain async functions + one exported QUERY_KEYS object,
// consumed inline by useQuery. No query-hook layer exists in this repo.

import { supabase } from '../../lib/supabase';

// Every RPC here ships in 202608281000_phase162_student_drills.sql. Until the
// user re-runs `supabase gen types typescript` AFTER applying it, the typed
// client's RPC-name union excludes them, so we narrow through `unknown` — the
// same escape hatch as ./practice-api.ts (KNOWN_ISSUES #12).
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export type PracticeSkill = 'listening' | 'reading' | 'writing' | 'speaking';
export type FeedbackPolicy = 'instant' | 'on_submit' | 'never';

export interface PracticeTypeCard {
  type_code: string;
  set_count: number;
  question_count: number;
  /** Percent, or null when the student hasn't met this type yet (or has met it
   *  only in Writing/Speaking, which has no per-question correctness). */
  accuracy: number | null;
  answered: number;
}

export interface PracticeSetSummary {
  test_id: string;
  title: string;
  skill: PracticeSkill;
  type_codes: string[];
  primary_type_code: string | null;
  difficulty: string;
  timed: boolean;
  is_untimed: boolean;
  duration_minutes: number | null;
  feedback_policy: FeedbackPolicy;
  show_stimulus: boolean;
  question_count: number;
  attempts: number;
  /** Best score as a percentage — a Reading/Listening drill has no band, so
   *  this is its score. Null for Writing/Speaking. */
  best_pct: number | null;
  /** The latest published band — a Writing/Speaking set's score (Phase 170).
   *  Absent before the phase-170 migration is applied. */
  last_band?: number | null | undefined;
  /** An attempt is still with the AI or a teacher (Phase 170). */
  awaiting_marking?: boolean | undefined;
  last_attempt_at: string | null;
  /** Set when the student walked away mid-drill; "Continue" rather than "Start". */
  open_attempt_id: string | null;
}

export interface TypeAccuracyRow {
  type_code: string;
  skill: PracticeSkill;
  answered: number;
  correct: number;
  accuracy: number | null;
  /** How many of these the student checked as they went. Counted in `accuracy`
   *  like any other answer — you cannot check one until you have committed an
   *  answer, and checking locks it — but reported separately as context. */
  assisted: number;
  last_answered_at: string | null;
}

export interface DrillReviewItem {
  question_id: string;
  number: number;
  type_code: string | null;
  prompt: string;
  /** Speaking part / Writing task the row belongs to (Phase 170). Null for
   *  Reading/Listening rows, and absent before the phase-170 migration. */
  part_index?: number | null | undefined;
  task_index?: number | null | undefined;
  your_answer: string | null;
  is_correct: boolean | null;
  revealed: boolean;
  correct_answer: string | null;
  explanation: string | null;
}

export interface DrillReview {
  attempt_id: string;
  feedback_policy: FeedbackPolicy;
  title: string;
  skill: PracticeSkill;
  /** exam_attempts.status — 'grading' while a Writing/Speaking set is still
   *  with the AI or a teacher (Phase 170; absent before its migration). */
  status?: string | undefined;
  review: DrillReviewItem[];
}

export const SKILL_PRACTICE_QUERY_KEYS = {
  types: (skill: string) => ['student', 'practice_types', skill] as const,
  sets: (skill: string, typeCode: string | null) =>
    ['student', 'practice_sets', skill, typeCode] as const,
  accuracy: (skill: string | null) => ['student', 'type_accuracy', skill] as const,
  review: (attemptId: string) => ['student', 'drill_review', attemptId] as const,
  attemptWeakTypes: (attemptId: string) => ['student', 'attempt_weak_types', attemptId] as const,
  mistakes: (skill: string | null, typeCode: string | null) =>
    ['student', 'mistakes', skill, typeCode] as const,
};

/** One question the student got wrong, with the reason it was wrong. */
export interface MistakeRow {
  attempt_id: string;
  question_id: string;
  test_title: string;
  skill: PracticeSkill;
  type_code: string;
  number: number;
  prompt: string;
  your_answer: string | null;
  correct_answer: string | null;
  explanation: string | null;
  /** They had already been shown the answer when they answered it — a
   *  different kind of mistake, and worth knowing while revising. */
  was_revealed: boolean;
  answered_at: string;
}

/** Types the student got wrong on ONE attempt, worst first. */
export interface AttemptWeakType {
  type_code: string;
  skill: PracticeSkill;
  wrong: number;
  total: number;
}

/** A missing RPC (migration not applied yet) reads as "nothing published yet"
 *  rather than a red screen — the new section renders its empty state. */
function isMissingFunction(message: string): boolean {
  return /could not find the function|does not exist|PGRST202/i.test(message);
}

export async function listPracticeTypes(skill: PracticeSkill): Promise<PracticeTypeCard[]> {
  const { data, error } = await rpcUnknown.rpc('list_practice_types', { p_skill: skill });
  if (error !== null) {
    if (isMissingFunction(error.message)) return [];
    throw new Error(error.message);
  }
  return (data as PracticeTypeCard[] | null) ?? [];
}

export async function listPracticeSets(
  skill: PracticeSkill,
  typeCode: string | null,
): Promise<PracticeSetSummary[]> {
  const { data, error } = await rpcUnknown.rpc('list_practice_sets', {
    p_skill: skill,
    p_type_code: typeCode,
  });
  if (error !== null) {
    if (isMissingFunction(error.message)) return [];
    throw new Error(error.message);
  }
  return (data as PracticeSetSummary[] | null) ?? [];
}

export async function getTypeAccuracy(skill: PracticeSkill | null): Promise<TypeAccuracyRow[]> {
  const { data, error } = await rpcUnknown.rpc('student_type_accuracy', { p_skill: skill });
  if (error !== null) {
    if (isMissingFunction(error.message)) return [];
    throw new Error(error.message);
  }
  return (data as TypeAccuracyRow[] | null) ?? [];
}

export async function getMistakes(
  skill: PracticeSkill | null,
  typeCode: string | null,
): Promise<MistakeRow[]> {
  const { data, error } = await rpcUnknown.rpc('student_mistakes', {
    p_skill: skill,
    p_type_code: typeCode,
  });
  if (error !== null) {
    if (isMissingFunction(error.message)) return [];
    throw new Error(error.message);
  }
  return (data as MistakeRow[] | null) ?? [];
}

export async function getAttemptWeakTypes(attemptId: string): Promise<AttemptWeakType[]> {
  const { data, error } = await rpcUnknown.rpc('student_attempt_weak_types', {
    p_attempt_id: attemptId,
  });
  if (error !== null) {
    // Absent RPC (migration not applied) or an unpublished result reads as
    // "nothing to suggest" — the module renders nothing rather than erroring
    // on a screen the student came to for their marks.
    return [];
  }
  return (data as AttemptWeakType[] | null) ?? [];
}

export async function getDrillReview(attemptId: string): Promise<DrillReview> {
  const { data, error } = await rpcUnknown.rpc('student_drill_review', {
    p_attempt_id: attemptId,
  });
  if (error !== null) throw new Error(error.message);
  if (data === null || typeof data !== 'object') {
    throw new Error('student_drill_review returned no data');
  }
  return data as DrillReview;
}

/** One of the caller's own attempts at a test (Phase 175) — the per-set
 *  history behind "Best 80% · 3 attempts". */
export interface TestAttemptRow {
  attempt_id: string;
  started_at: string;
  submitted_at: string | null;
  status: string;
  graded_count: number;
  correct_count: number;
  /** Percent of graded answers correct; null when nothing was auto-graded. */
  pct: number | null;
  /** The published band for a Writing/Speaking set; null otherwise. */
  band: number | null;
  published: boolean;
}

export const testAttemptsKey = (testId: string) => ['student', 'test_attempts', testId] as const;

export async function listTestAttempts(testId: string): Promise<TestAttemptRow[]> {
  const { data, error } = await rpcUnknown.rpc('student_list_test_attempts', {
    p_test_id: testId,
  });
  if (error !== null) {
    if (isMissingFunction(error.message)) return [];
    throw new Error(error.message);
  }
  return (data as TestAttemptRow[] | null) ?? [];
}
